export interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string>;
  headers?: Record<string, string>;
}

export interface ApiError extends Error {
  status: number;
  data: unknown;
}

export interface ApiClientOptions {
  baseUrl?: string;
  headers?: () => Record<string, string>;
  onError?: (err: ApiError) => unknown;
}

export interface ApiClient {
  baseUrl: string;
  get: <T = unknown>(path: string, opts?: Omit<RequestOptions, 'method'>) => Promise<T>;
  post: <T = unknown>(path: string, opts?: Omit<RequestOptions, 'method'>) => Promise<T>;
  put: <T = unknown>(path: string, opts?: Omit<RequestOptions, 'method'>) => Promise<T>;
  patch: <T = unknown>(path: string, opts?: Omit<RequestOptions, 'method'>) => Promise<T>;
  delete: <T = unknown>(path: string, opts?: Omit<RequestOptions, 'method'>) => Promise<T>;
  request: <T = unknown>(path: string, opts?: RequestOptions) => Promise<T>;
  subscribe: () => WebSocket | null;
}

export function createApiClient({ baseUrl = '', headers: headersFn, onError }: ApiClientOptions = {}): ApiClient {
  async function request<T = unknown>(path: string, { method = 'GET', body, query, headers: extra }: RequestOptions = {}): Promise<T> {
    let url = `${baseUrl}${path}`;

    if (query) {
      const qs = new URLSearchParams(query).toString();
      if (qs) url += `?${qs}`;
    }

    const headers: Record<string, string> = {
      ...(headersFn ? headersFn() : {}),
      ...(extra || {}),
    };

    const init: RequestInit = { method, headers };

    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }

    const res = await fetch(url, init);

    // 204 No Content
    if (res.status === 204) return null as T;

    const data = res.headers.get('content-type')?.includes('json')
      ? await res.json()
      : await res.text();

    if (!res.ok) {
      const err = Object.assign(new Error(data?.error || data?.message || res.statusText), {
        status: res.status,
        data,
      });
      if (onError) return onError(err) as T;
      throw err;
    }

    return data as T;
  }

  return {
    baseUrl,
    get:    <T = unknown>(path: string, opts?: Omit<RequestOptions, 'method'>) => request<T>(path, { ...opts, method: 'GET' }),
    post:   <T = unknown>(path: string, opts?: Omit<RequestOptions, 'method'>) => request<T>(path, { ...opts, method: 'POST' }),
    put:    <T = unknown>(path: string, opts?: Omit<RequestOptions, 'method'>) => request<T>(path, { ...opts, method: 'PUT' }),
    patch:  <T = unknown>(path: string, opts?: Omit<RequestOptions, 'method'>) => request<T>(path, { ...opts, method: 'PATCH' }),
    delete: <T = unknown>(path: string, opts?: Omit<RequestOptions, 'method'>) => request<T>(path, { ...opts, method: 'DELETE' }),
    request,
    subscribe: (): WebSocket | null => {
      let wsUrl: string | undefined;
      if (baseUrl.startsWith('http')) {
        wsUrl = baseUrl.replace(/^http/, 'ws');
      } else if (typeof window !== 'undefined') {
        const origin = window.location.origin;
        wsUrl = origin.replace(/^http/, 'ws') + baseUrl;
      } else {
        return null;
      }
      return new WebSocket(wsUrl);
    },
  };
}
